import { defineComponent, inject, ref, onMounted, computed, createElementBlock, openBlock, createElementVNode, createVNode, unref, normalizeClass, toDisplayString, createCommentVNode, Fragment, renderList, withDirectives, withCtx, createTextVNode } from "vue";
import { Product, Money, ProductIngredient } from "app-core";
import { useI18n } from "vue-i18n";
import UButton from "@nuxt/ui/components/Button.vue";
import UInput from "@nuxt/ui/components/Input.vue";
import UAlert from "@nuxt/ui/components/Alert.vue";
import { maskito } from "@maskito/vue";
import { useMaskito } from "./index51.js";
import ProductSearch from "./index38.js";
import { useSheetLayer, SHEET_LEADING_CONTROL_CLASS } from "./index48.js";
const _hoisted_1 = { class: "flex min-h-0 flex-1 flex-col" };
const _hoisted_2 = { class: "shrink-0" };
const _hoisted_3 = { class: "flex min-h-12 items-center gap-2 px-2 pt-2" };
const _hoisted_4 = { class: "flex-1 truncate text-panel-title" };
const _hoisted_5 = { class: "px-4" };
const _hoisted_6 = { class: "min-h-0 flex-1 overflow-y-auto" };
const _hoisted_7 = { class: "p-4" };
const _hoisted_8 = {
  key: 0,
  class: "px-4"
};
const _hoisted_9 = { class: "min-w-0 flex-1 truncate" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ProductIngredients",
  props: {
    product: {
      type: Object,
      default: () => void 0
    },
    onSave: {
      type: Function,
      default: () => {
      }
    },
    onCancel: {
      type: Function,
      default: () => {
      }
    },
    /** Full product list override (admin's server-fetched list) — default Product.all(). */
    products: {
      type: Array,
      default: () => void 0
    }
  },
  setup(__props) {
    const props = __props;
    const backoffice = inject("kontyBackoffice", false);
    const { nested } = useSheetLayer();
    const footerClass = "flex min-h-17 shrink-0 items-center gap-2 px-4 py-3 *:flex-1" + (backoffice ? " sm:justify-end sm:*:flex-none" : "");
    const buttonSize = backoffice ? "md" : "lg";
    const productsList = () => props.products ?? Product.all();
    const findProduct = (id) => props.products ? props.products.find((p) => p.id === id) : Product.find(id);
    const { t } = useI18n();
    const { createNumberMask } = useMaskito();
    const quantityMask = createNumberMask({ precision: 3, min: 1e-3 });
    const searchProduct = ref(null);
    const inputRefs = ref([]);
    onMounted(() => {
      setTimeout(() => {
        var _a;
        return (_a = searchProduct.value) == null ? void 0 : _a.focus();
      }, 200);
    });
    const savedIngredients = () => {
      var _a, _b;
      const rows = (_a = props.product) == null ? void 0 : _a.ingredients;
      return rows ? rows.map((row) => new ProductIngredient(row.pivot)) : ((_b = props.product) == null ? void 0 : _b.getIngredients()) || [];
    };
    const getAllItems = () => {
      const ingredients = savedIngredients();
      const items = ingredients.map((i) => ({ id: i.id || void 0, product: findProduct(i.product_id), quantity: i.getQuantity() }));
      return items.filter((i) => i.product).map((i) => ({ id: i.id, product: i.product, quantity: i.quantity }));
    };
    const selected = ref(getAllItems());
    const availableIngredients = computed(() => {
      return productsList().filter((product) => {
        var _a;
        return !selected.value.some((item) => item.product.id == product.id) && product.id !== ((_a = props.product) == null ? void 0 : _a.id) && !product.is_made && !product.is_sold;
      });
    });
    function selectText(inputIndex) {
      var _a;
      const inputEl = (_a = inputRefs.value[inputIndex]) == null ? void 0 : _a.inputRef;
      inputEl == null ? void 0 : inputEl.setSelectionRange(0, inputEl.value.length);
    }
    function selectProduct(product, quantity) {
      setTimeout(() => {
        var _a, _b;
        const index = selected.value.findIndex((item) => item.product.id === product.id);
        (_b = (_a = inputRefs.value[index]) == null ? void 0 : _a.inputRef) == null ? void 0 : _b.focus();
      }, 200);
      const existingItem = selected.value.find((item) => item.product.id === product.id);
      if (existingItem) {
        existingItem.quantity = Money.parse(existingItem.quantity + quantity).toRoundedUnit(3);
        return;
      }
      selected.value = [{ id: void 0, product, quantity }, ...selected.value];
    }
    function removeItem(itemIndex) {
      selected.value.splice(itemIndex, 1);
    }
    function save() {
      const ingredients = selected.value.filter((item) => !!item.quantity).map((item) => {
        return { product_id: item.product.id, quantity: item.quantity };
      });
      if (!ingredients.length) return;
      props.onSave(ingredients);
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1, [
        createElementVNode("div", _hoisted_2, [
          createElementVNode("div", _hoisted_3, [
            createVNode(UButton, {
              color: "neutral",
              variant: "ghost",
              size: "md",
              class: normalizeClass(unref(backoffice) ? void 0 : unref(SHEET_LEADING_CONTROL_CLASS)),
              icon: unref(nested) ? "i-lucide-chevron-left" : "i-lucide-x",
              "aria-label": unref(nested) ? unref(t)("general.back") : unref(t)("general.close"),
              onClick: __props.onCancel
            }, null, 8, ["class", "icon", "aria-label", "onClick"]),
            createElementVNode("h2", _hoisted_4, toDisplayString(unref(t)("general.ingredient", 2)), 1)
          ]),
          createElementVNode("div", _hoisted_5, [
            createVNode(ProductSearch, {
              ref_key: "searchProduct",
              ref: searchProduct,
              class: "w-full",
              "in-modal": "",
              placeholder: unref(t)("general.search"),
              products: availableIngredients.value,
              onSelect: _cache[0] || (_cache[0] = ($event) => selectProduct($event.product, $event.quantity))
            }, null, 8, ["placeholder", "products"])
          ])
        ]),
        createElementVNode("div", _hoisted_6, [
          createElementVNode("div", _hoisted_7, [
            createVNode(UAlert, {
              color: "info",
              variant: "soft",
              icon: "i-lucide-info",
              description: unref(t)("products.no_quantity")
            }, null, 8, ["description"])
          ]),
          selected.value.length ? (openBlock(), createElementBlock("div", _hoisted_8, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(selected.value, (item, index) => {
              return openBlock(), createElementBlock("div", {
                key: "" + item.product.id,
                class: normalizeClass(["flex items-center gap-2 py-1", { "border-b border-default": index !== selected.value.length - 1 }])
              }, [
                createVNode(UButton, {
                  color: "error",
                  variant: "ghost",
                  icon: "i-lucide-x",
                  "aria-label": unref(t)("general.delete"),
                  onClick: ($event) => removeItem(index)
                }, null, 8, ["aria-label", "onClick"]),
                createElementVNode("span", _hoisted_9, toDisplayString(item.product.name), 1),
                withDirectives(createVNode(UInput, {
                  ref_for: true,
                  ref: (el) => {
                    if (el) inputRefs.value[index] = el;
                  },
                  modelValue: item.quantity,
                  "onUpdate:modelValue": ($event) => item.quantity = $event,
                  class: "w-28",
                  ui: { base: "text-right" },
                  inputmode: "decimal",
                  onFocus: ($event) => selectText(index)
                }, null, 8, ["modelValue", "onUpdate:modelValue", "onFocus"]), [
                  [unref(maskito), unref(quantityMask)]
                ]),
                createElementVNode("div", {
                  class: normalizeClass(["min-w-[35px]", { "text-error": !item.quantity }])
                }, toDisplayString(item.product.getUnitOfMeasure()), 3)
              ], 2);
            }), 128))
          ])) : createCommentVNode("", true)
        ]),
        createElementVNode("div", {
          class: normalizeClass(footerClass)
        }, [
          createVNode(UButton, {
            color: "neutral",
            variant: "outline",
            size: unref(buttonSize),
            onClick: __props.onCancel
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.back")), 1)
            ]),
            _: 1
          }, 8, ["size", "onClick"]),
          createVNode(UButton, {
            color: "primary",
            variant: "solid",
            size: unref(buttonSize),
            icon: "i-lucide-check",
            disabled: !selected.value.length || selected.value.some((i) => !i.quantity),
            onClick: save
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.save")), 1)
            ]),
            _: 1
          }, 8, ["size", "disabled"])
        ])
      ]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index110.js.map
